import { Helmet } from 'react-helmet-async'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { useNumericParams } from '@domain-group/react-hooks'

import useCampaignContact from '@hooks/useCampaignContact'
import useCampaignContacts from '@hooks/useCampaignContacts'
import useCampaignSelector from '@hooks/useCampaignSelector'
import useCurrentUser from '@hooks/useCurrentUser'

import CampaignContactListItem from '@components/CampaignContacts/CampaignContactListItem'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'
import propertyDetailsTabBarItems from '@sections/Client/packs/Campaign/utils/generatePropertyDetailsTabBarItems'

import CampaignContactBlankList from '../CampaignContactBlankList'

const PageContent = () => {
  const { campaignId: campaign_id } = useNumericParams()

  const campaignContactsPayload = useCampaignContacts({
    filters: {
      campaign_id,
    },
    performHttpRequests: !!campaign_id,
    requestOptions: defaultRequestOptions.campaignContact,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredCampaignContacts,
    filteredCampaignContactsCount,
    hasFilteredCampaignContacts,
    loading,
  } = campaignContactsPayload

  const { callbacks: { createCampaignContactModal } } = useCampaignContact({ campaign_id })

  const { currentUser } = useCurrentUser()

  const { campaign, campaign: { address } } = useCampaignSelector()

  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Contacts</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => createCampaignContactModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="contacts"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={propertyDetailsTabBarItems(campaign_id, campaignPermissions)}
        title="Contacts"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.contacts.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignContacts && <CampaignContactBlankList />}

            {!loading && hasFilteredCampaignContacts && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Campaign Contacts">
                <Box flexDirection="column">
                  {filteredCampaignContacts.map(campaignContact => (
                    <CampaignContactListItem campaignContact={campaignContact} key={campaignContact.id} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCampaignContactsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.contacts.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
